import React, { useState } from 'react';
import { useFitStore } from '../store/useFitStore';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';

const ProgressVault: React.FC = () => {
  const { progressPhotos, uploadProgressPhoto } = useFitStore();
  const [isUploading, setIsUploading] = useState(false);
  const [category, setCategory] = useState<'front' | 'side' | 'back'>('front');
  const [weight, setWeight] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      await uploadProgressPhoto(file, category, weight);
      alert('Photo uploaded successfully!');
      setWeight('');
    } catch (err: any) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const sortedPhotos = [...progressPhotos].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const [compareA, setCompareA] = useState<string | null>(null);
  const [compareB, setCompareB] = useState<string | null>(null);
  const photoA = sortedPhotos.find((p) => p.id === compareA) || null;
  const photoB = sortedPhotos.find((p) => p.id === compareB) || null;
  const clearCompare = () => { setCompareA(null); setCompareB(null); };

  return (
    <div className="main-content">
      <header style={{ marginBottom: 'var(--spacing-xl)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="text-accent" style={{ fontSize: '2rem' }}>Progress Vault</h1>
          <p className="text-muted">Track your physical transformation.</p>
        </div>
        {sortedPhotos.length >= 2 && (
          <span className="text-[10px] tracking-[0.08em] font-semibold text-fit-muted self-center">Tap two photos to compare</span>
        )}
      </header>
      {(photoA || photoB) && (
        <Card variant="glass" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
            <h2 style={{ fontSize: '0.95rem', fontWeight: 800 }}>Before / After</h2>
            <button onClick={clearCompare} className="text-[11px] text-fit-muted hover:text-white underline underline-offset-2">Clear</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
            {[photoA, photoB].map((ph, i) => (
              <div key={i} style={{ minHeight: 180, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--glass-border)', background: 'var(--bg-tertiary)', display: 'flex', flexDirection: 'column' }}>
                {ph ? (
                  <>
                    <img src={ph.url} alt={`${ph.category} ${ph.date}`} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                    <div style={{ padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--accent-primary)' }}>{i === 0 ? 'Before' : 'After'} · {ph.category}</span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{new Date(ph.date).toLocaleDateString()}{ph.weight ? ` · ${ph.weight}` : ''}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <span className="text-muted" style={{ fontSize: '12px' }}>{i === 0 ? 'Select a “before” photo below' : 'Select an “after” photo below'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card variant="glass" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-lg)' }}>Add New Photo</h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
            {(['front', 'side', 'back'] as const).map(cat => (
              <Button 
                key={cat}
                variant={category === cat ? 'primary' : 'glass'}
                size="sm"
                onClick={() => setCategory(cat)}
                style={{ flex: 1 }}
              >
                {cat.toUpperCase()}
              </Button>
            ))}
          </div>

          <Input 
            label="Current Weight (optional)" 
            type="number" 
            value={weight} 
            onChange={e => setWeight(e.target.value)} 
            placeholder="LBS"
          />

          <div style={{ position: 'relative' }}>
            <input 
              type="file" 
              accept="image/*" 
              capture="environment"
              onChange={handleFileChange}
              style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
              disabled={isUploading}
            />
            <Button fullWidth isLoading={isUploading} variant="secondary">
              {isUploading ? 'UPLOADING...' : 'SELECT / TAKE PHOTO'}
            </Button>
          </div>
        </div>
      </Card>

      <section>
        <h2 style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-md)' }}>Your Journey</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
          {sortedPhotos.length > 0 ? sortedPhotos.map(photo => {
            const isSelected = photo.id === compareA || photo.id === compareB;
            const onSelect = () => {
              if (compareA === photo.id || compareB === photo.id) {
                if (compareA === photo.id) setCompareA(null); else setCompareB(null);
                return;
              }
              if (!compareA) setCompareA(photo.id);
              else if (!compareB) setCompareB(photo.id);
              else { setCompareA(photo.id); setCompareB(null); }
            };
            return (
            <Card key={photo.id} padding="0" style={{ overflow: 'hidden', position: 'relative', outline: isSelected ? '2px solid var(--accent-primary)' : 'none', cursor: 'pointer' }} onClick={onSelect}>
              <img src={photo.url} alt={photo.category} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
              <div style={{
                position: 'absolute',
                bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.7)',
                padding: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--accent-primary)' }}>{photo.category}</span>
                <span style={{ fontSize: '0.7rem', color: 'white' }}>{new Date(photo.date).toLocaleDateString()}</span>
              </div>
            </Card>
          );}) : (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 'var(--spacing-xl)' }}>
              <p className="text-muted">No photos yet. Start your vault today!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProgressVault;
